import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Input,
  InputBase,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add,
  Folder as FolderIcon,
  Create,
  Check,
  Close,
  Delete as DeleteIcon,
  Refresh,
  DragIndicator,
  TrendingDown,
} from '@mui/icons-material';
import { Snackbar } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  pointerWithin,
  DragOverlay,
  useDndContext,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { StyledContainer, StyledPaper, StyledChannelChip } from './styles';
import { API_URL } from './config';
import axios from 'axios';
import { trackEvent } from './analytics';
import TerminationModal from './TerminationModal';

const ARCHIVED_TAB_ID = '__archived__';

// Droppable Tab Component
function DroppableTab({
  value,
  label,
  isEditing,
  editingName,
  canDelete,
  onDoubleClick,
  onEditChange,
  onEditSave,
  onEditCancel,
  onDelete,
  ...props
}) {
  const { isOver, setNodeRef } = useDroppable({
    id: value,
    data: { type: 'tab', name: label },
  });
  const { active } = useDndContext();
  const isDraggingFolder = active?.data?.current?.type === 'folder';
  const showHighlight = isOver && isDraggingFolder;

  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onEditSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onEditCancel();
    }
  };

  const style = {
    opacity: showHighlight ? 1 : 0.7,
    backgroundColor: showHighlight ? 'rgba(99, 102, 241, 0.2)' : 'transparent',
    border: showHighlight ? '2px dashed #6366f1' : '2px solid transparent',
    transform: showHighlight ? 'scale(1.05)' : 'scale(1)',
    transition: 'all 0.2s ease',
    zIndex: showHighlight ? 1 : 'auto',
    borderRadius: '8px',
    fontWeight: showHighlight ? 'bold' : 'normal',
    color: showHighlight ? '#6366f1' : 'inherit',
  };

  if (isEditing) {
    return (
      <Box
        ref={setNodeRef}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          minHeight: 48,
          px: 1.5,
          position: 'relative',
        }}
      >
        <InputBase
          inputRef={inputRef}
          value={editingName}
          onChange={(e) => onEditChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={onEditSave}
          sx={{
            fontSize: '0.875rem',
            fontWeight: 500,
            color: 'text.primary',
            minWidth: 100,
            maxWidth: 200,
            px: 1.5,
            py: 0.75,
            backgroundColor: 'rgba(99, 102, 241, 0.08)',
            borderRadius: '6px',
            border: '1.5px solid rgba(99, 102, 241, 0.3)',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: 'rgba(99, 102, 241, 0.12)',
              borderColor: 'rgba(99, 102, 241, 0.5)',
            },
            '&:focus': {
              backgroundColor: 'rgba(99, 102, 241, 0.15)',
              borderColor: '#6366f1',
              boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.1)',
            },
            '&::placeholder': {
              opacity: 0.6,
            },
          }}
        />
      </Box>
    );
  }

  return (
    <Tab
      ref={setNodeRef}
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box component="span" sx={{ lineHeight: 1.2 }}>
            {label}
          </Box>
          <Tooltip title={canDelete ? 'Delete tab' : 'You must keep at least one tab'}>
            <span>
              <IconButton
                size="small"
                disabled={!canDelete}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDelete();
                }}
                sx={{
                  p: 0.25,
                  color: canDelete ? 'text.secondary' : 'text.disabled',
                  '&:hover': {
                    color: '#ef4444',
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  },
                }}
              >
                <Close sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      }
      value={value}
      onDoubleClick={onDoubleClick}
      {...props}
      sx={{ ...props.sx, ...style }}
    />
  );
}

// Sortable Tab Component
function SortableTab({
  value,
  label,
  isEditing,
  editingName,
  canDelete,
  onDoubleClick,
  onEditChange,
  onEditSave,
  onEditCancel,
  onDelete,
  ...props
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } =
    useSortable({
      id: value,
      data: { type: 'tab', name: label },
      disabled: isEditing,
    });

  const { active } = useDndContext();
  const isDraggingFolder = active?.data?.current?.type === 'folder';
  const showHighlight = isOver && isDraggingFolder;

  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onEditSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onEditCancel();
    }
  };

  const style = {
    transform: CSS.Transform.toString(transform) || (showHighlight ? 'scale(1.05)' : 'scale(1)'),
    transition: transition || 'all 0.2s ease',
    opacity: isDragging ? 0.5 : showHighlight ? 1 : 1,
    backgroundColor: showHighlight ? 'rgba(99, 102, 241, 0.2)' : 'transparent',
    border: showHighlight ? '2px dashed #6366f1' : '2px solid transparent',
    borderRadius: '8px',
    fontWeight: showHighlight ? 'bold' : 'normal',
    color: showHighlight ? '#6366f1' : 'inherit',
    cursor: isEditing ? 'text' : 'grab',
    zIndex: isDragging ? 1000 : 'auto',
  };

  if (isEditing) {
    return (
      <Box
        ref={setNodeRef}
        style={style}
        {...attributes}
        // Disable listeners when editing to allow text selection
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          minHeight: 48,
          px: 1.5,
          position: 'relative',
        }}
      >
        <InputBase
          inputRef={inputRef}
          value={editingName}
          onChange={(e) => onEditChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={onEditSave}
          sx={{
            fontSize: '0.875rem',
            fontWeight: 500,
            color: 'text.primary',
            minWidth: 100,
            maxWidth: 200,
            px: 1.5,
            py: 0.75,
            backgroundColor: 'rgba(99, 102, 241, 0.08)',
            borderRadius: '6px',
            border: '1.5px solid rgba(99, 102, 241, 0.3)',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: 'rgba(99, 102, 241, 0.12)',
              borderColor: 'rgba(99, 102, 241, 0.5)',
            },
            '&:focus': {
              backgroundColor: 'rgba(99, 102, 241, 0.15)',
              borderColor: '#6366f1',
              boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.1)',
            },
            '&::placeholder': {
              opacity: 0.6,
            },
          }}
        />
      </Box>
    );
  }

  return (
    <Tab
      ref={setNodeRef}
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box component="span" sx={{ lineHeight: 1.2 }}>
            {label}
          </Box>
          <Tooltip title={canDelete ? 'Delete tab' : 'You must keep at least one tab'}>
            <span>
              <IconButton
                size="small"
                disabled={!canDelete}
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDelete();
                }}
                sx={{
                  p: 0.25,
                  color: canDelete ? 'text.secondary' : 'text.disabled',
                  '&:hover': {
                    color: '#ef4444',
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  },
                }}
              >
                <Close sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      }
      value={value}
      onDoubleClick={onDoubleClick}
      {...props}
      {...attributes}
      {...listeners}
      sx={{ ...props.sx, ...style }}
    />
  );
}

// Sortable Folder Card Component
function SortableFolderCard({
  folder,
  openEditDialog,
  onDeleteFolder,
  onOpenTerminationModal,
  showTerminationSummary = false,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: folder._id,
    data: { type: 'folder', folder }, // Add data payload
    disabled: folder.isDeleted === true,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    height: '100%',
  };

  const terminatedCount = folder.terminatedCount || 0;
  const totalChannels = folder.channelCount || 0;
  const terminatedPercentage = totalChannels > 0 ? (terminatedCount / totalChannels) * 100 : 0;

  return (
    <Grid
      item
      xs={12}
      sm={6}
      md={3}
      ref={setNodeRef}
      style={style}
      sx={{ minWidth: '22%', flex: '0 0 22%' }}
    >
      <Tooltip title={folder.name} arrow placement="top">
        <Box
          component="a"
          href={`/folders/${folder._id}`}
          sx={{
            textDecoration: 'none',
            color: 'inherit',
            display: 'block',
            height: '100%',
            position: 'relative',
          }}
        >
          {!folder.isDeleted && (
            <Box
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 2,
                display: 'flex',
                gap: 0,
              }}
            >
              <Tooltip title="Edit folder">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openEditDialog(folder);
                  }}
                  sx={{
                    color: 'primary.main',
                    '&:hover': {
                      background: 'rgba(99, 102, 241, 0.1)',
                    },
                  }}
                >
                  <Create />
                </IconButton>
              </Tooltip>
              <Tooltip title="Archive folder">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDeleteFolder(folder);
                  }}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      color: '#ef4444',
                      background: 'rgba(239, 68, 68, 0.1)',
                    },
                  }}
                >
                  <Close />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          <Card
            sx={{
              height: '100%',
              minWidth: '22%',
              background:
                'linear-gradient(145deg, rgba(30, 41, 59, 0.7) 0%, rgba(15, 23, 42, 0.7) 100%)',
              border: '1px solid rgba(148, 163, 184, 0.12)',
              backdropFilter: 'blur(6px)',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 20px 60px rgba(99, 102, 241, 0.25)',
                transition: 'all 0.3s ease',
              },
            }}
          >
            <CardContent sx={{ p: 3, pr: 10 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                {/* Drag Handle */}
                {!folder.isDeleted && (
                  <Box
                    {...attributes}
                    {...listeners}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    sx={{
                      cursor: 'grab',
                      mr: 1,
                      display: 'flex',
                      alignItems: 'center',
                      color: 'text.secondary',
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    <DragIndicator fontSize="small" />
                  </Box>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flex: 1,
                    minWidth: 0,
                    textDecoration: 'none',
                    color: 'inherit',
                    cursor: 'pointer',
                  }}
                >
                  <Box
                    sx={{
                      padding: '12px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: 28,
                      flexShrink: 0,
                    }}
                  >
                    <FolderIcon />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      noWrap
                      sx={{
                        color: '#ff3669',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: '100%',
                      }}
                    >
                      {folder.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Updated {new Date(folder.updatedAt || folder.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {typeof folder.channelCount === 'number' && typeof folder.videoCount === 'number'
                    ? `${folder.channelCount} ${folder.channelCount === 1 ? 'channel' : 'channels'} · ${folder.videoCount.toLocaleString()} ${folder.videoCount === 1 ? 'video' : 'videos'}`
                    : 'Folder'}
                </Typography>
                {showTerminationSummary && totalChannels > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Box
                      sx={{
                        p: 0.75,
                        borderRadius: 1,
                        background: 'rgba(239, 68, 68, 0.06)',
                        border: '1px solid rgba(239, 68, 68, 0.15)',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.75,
                          mb: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box
                              sx={{
                                width: 5,
                                height: 5,
                                borderRadius: '50%',
                                background: terminatedCount === 0 ? '#22c55e' : '#ef4444',
                                opacity: 0.7,
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                color: terminatedCount === 0 ? '#22c55e' : '#ef4444',
                                fontWeight: 500,
                                fontSize: '0.7rem',
                              }}
                            >
                              {terminatedCount} terminated
                            </Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              fontSize: '0.65rem',
                            }}
                          >
                            of {totalChannels}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ mb: 0.75 }}>
                        <Box
                          sx={{
                            width: '100%',
                            height: 3,
                            borderRadius: 2,
                            background: 'rgba(148, 163, 184, 0.08)',
                            overflow: 'hidden',
                            position: 'relative',
                            display: 'flex',
                          }}
                        >
                          {terminatedPercentage > 0 && (
                            <Box
                              sx={{
                                width: `${terminatedPercentage}%`,
                                height: '100%',
                                background: '#ef4444',
                                transition: 'width 0.3s ease',
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<TrendingDown sx={{ fontSize: '0.875rem' }} />}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onOpenTerminationModal(folder);
                        }}
                        sx={{
                          width: '100%',
                          color: terminatedCount === 0 ? 'rgba(34, 197, 94, 0.7)' : 'rgba(239, 68, 68, 0.75)',
                          fontWeight: 400,
                          fontSize: '0.7rem',
                          py: 0.5,
                          textTransform: 'none',
                          borderRadius: 1,
                          minHeight: 'auto',
                          '&:hover': {
                            background: terminatedCount === 0 ? 'rgba(34, 197, 94, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                          },
                        }}
                      >
                        View Details
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Tooltip>
    </Grid>
  );
}

// Overlay Component for Dragged Item
function FolderOverlay({ folder }) {
  return (
    <Card
      sx={{
        height: '100%',
        width: '100%',
        background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.9) 0%, rgba(15, 23, 42, 0.9) 100%)',
        border: '1px solid rgba(99, 102, 241, 0.5)',
        boxShadow: '0 20px 60px rgba(99, 102, 241, 0.4)',
        cursor: 'grabbing',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              padding: '12px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: 28,
            }}
          >
            <FolderIcon />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#ff3669' }}>
              {folder.name}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function ChannelTracker() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [folders, setFolders] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingTabs, setLoadingTabs] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addTabDialogOpen, setAddTabDialogOpen] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [selectedTabId, setSelectedTabId] = useState(''); // For folder creation/edit
  const [tabName, setTabName] = useState('');
  const [editingFolder, setEditingFolder] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [toastOpen, setToastOpen] = useState(false);
  const [activeDragId, setActiveDragId] = useState(null); // Track active drag item
  const [editingTabId, setEditingTabId] = useState(null); // Track which tab is being edited
  const [editingTabName, setEditingTabName] = useState(''); // Track the editing tab name
  const [deleteTabDialogOpen, setDeleteTabDialogOpen] = useState(false);
  const [tabPendingDeletion, setTabPendingDeletion] = useState(null);
  const [moveFoldersToTabId, setMoveFoldersToTabId] = useState('');
  const [deletingTab, setDeletingTab] = useState(false);
  const [deleteFolderDialogOpen, setDeleteFolderDialogOpen] = useState(false);
  const [folderPendingDeletion, setFolderPendingDeletion] = useState(null);
  const [deletingFolder, setDeletingFolder] = useState(false);
  const [terminationModalOpen, setTerminationModalOpen] = useState(false);
  const [selectedFolderForTermination, setSelectedFolderForTermination] = useState(null);
  const [isZKeyHeld, setIsZKeyHeld] = useState(false);
  const [showTerminationSummary, setShowTerminationSummary] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    fetchTabs();
    fetchFolders();
  }, []);

  // Set active tab from URL or default to first tab when tabs load
  useEffect(() => {
    if (tabs.length > 0 && !activeTab) {
      const tabFromUrl = searchParams.get('tab');
      if (tabFromUrl && tabs.some((t) => t._id === tabFromUrl)) {
        // Tab from URL exists, use it
        setActiveTab(tabFromUrl);
      } else {
        // No tab in URL or tab doesn't exist, use first tab
        setActiveTab(tabs[0]._id);
      }
    }
  }, [tabs, activeTab, searchParams]);

  // Handle browser back/forward navigation
  useEffect(() => {
    if (tabs.length > 0) {
      const tabFromUrl = searchParams.get('tab');
      if (tabFromUrl && tabs.some((t) => t._id === tabFromUrl) && activeTab !== tabFromUrl) {
        setActiveTab(tabFromUrl);
      }
    }
  }, [searchParams, tabs, activeTab]);

  useEffect(() => {
    if (!isZKeyHeld && activeTab === ARCHIVED_TAB_ID && tabs.length > 0) {
      const fallbackTabId = tabs[0]._id;
      setActiveTab(fallbackTabId);
      setSearchParams({ tab: fallbackTabId }, { replace: true });
    }
  }, [isZKeyHeld, activeTab, tabs, setSearchParams]);

  // Track 'z' key hold state (for Fetch Latest Data button)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'z' || e.key === 'Z') {
        setIsZKeyHeld(true);
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'z' || e.key === 'Z') {
        setIsZKeyHeld(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Show termination summary on folder cards only when Z is held
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.key === 'z' || e.key === 'Z')) {
        setShowTerminationSummary(true);
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'z' || e.key === 'Z') {
        setShowTerminationSummary(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const fetchTabs = async () => {
    try {
      setLoadingTabs(true);
      const response = await axios.get(`${API_URL}/api/tabs`);
      setTabs(response.data);
    } catch (error) {
      console.error('Error fetching tabs:', error);
    } finally {
      setLoadingTabs(false);
    }
  };

  const fetchFolders = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const response = await axios.get(`${API_URL}/api/folders?includeDeleted=true`);
      setFolders(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching folders:', error);
      setError('Failed to fetch folders');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleAddFolder = async () => {
    if (!folderName.trim()) {
      setError('Folder name is required');
      return;
    }

    try {
      // Use selectedTabId if provided, otherwise use activeTab
      const tabIdToUse = selectedTabId || activeTab || null;

      const response = await axios.post(`${API_URL}/api/folders`, {
        name: folderName.trim(),
        tabId: tabIdToUse,
      });

      const newFolder = response.data;

      // Optimistically add the new folder to the state immediately
      setFolders((prevFolders) => {
        // Check if folder already exists (shouldn't happen, but safety check)
        if (prevFolders.some((f) => f._id === newFolder._id)) {
          return prevFolders;
        }
        // Add new folder and sort by order, then by createdAt
        const updatedFolders = [...prevFolders, newFolder];
        return updatedFolders.sort((a, b) => {
          if (a.tabId !== b.tabId) {
            // Group by tabId first
            const aTabId = a.tabId?.toString() || '';
            const bTabId = b.tabId?.toString() || '';
            if (aTabId !== bTabId) {
              return aTabId.localeCompare(bTabId);
            }
          }
          // Then sort by order
          if (a.order !== b.order) {
            return a.order - b.order;
          }
          // Finally by createdAt descending
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
      });

      // If folder was created with a different tab, switch to that tab
      if (newFolder.tabId && newFolder.tabId !== activeTab) {
        setActiveTab(newFolder.tabId);
        setSearchParams({ tab: newFolder.tabId }, { replace: true });
      }

      // Close dialog and clear form immediately for better UX
      setFolderName('');
      setSelectedTabId('');
      setAddDialogOpen(false);
      setError('');
      setSuccess('Folder created successfully!');
      setTimeout(() => setSuccess(''), 3000);

      trackEvent('create_folder', {
        scope: 'global',
        context: {},
        payload: { folderId: newFolder._id, name: newFolder.name, tabId: newFolder.tabId },
      });

      // Silently refetch folders in the background to ensure we have accurate counts
      // This won't show loading spinner, preventing the glitch
      fetchFolders(false).catch((err) => {
        console.error('Error silently refreshing folders:', err);
        // If silent refresh fails, do a full refresh with loading
        fetchFolders(true);
      });
    } catch (error) {
      console.error('Error adding folder:', error);
      setError(error.message || 'Failed to add folder');
    }
  };

  const handleAddTab = async () => {
    if (!tabName.trim()) {
      setError('Tab name is required');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/api/tabs`, {
        name: tabName.trim(),
      });

      const newTab = response.data;
      setTabs([...tabs, newTab]);
      setTabName('');
      setAddTabDialogOpen(false);
      setActiveTab(newTab._id); // Switch to new tab
      setSearchParams({ tab: newTab._id }, { replace: true }); // Update URL
      setSuccess('Tab created successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleTabDoubleClick = (tabId) => {
    const tab = tabs.find((t) => t._id === tabId);
    if (tab) {
      setEditingTabId(tabId);
      setEditingTabName(tab.name);
    }
  };

  const handleUpdateTab = async () => {
    if (!editingTabName.trim()) {
      setEditingTabId(null);
      setEditingTabName('');
      return;
    }

    try {
      const response = await axios.put(`${API_URL}/api/tabs/${editingTabId}`, {
        name: editingTabName.trim(),
      });

      const updatedTab = response.data;
      setTabs(tabs.map((t) => (t._id === editingTabId ? updatedTab : t)));
      setEditingTabId(null);
      setEditingTabName('');
      setSuccess('Tab updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.message || 'Failed to update tab');
      setEditingTabId(null);
      setEditingTabName('');
    }
  };

  const handleCancelTabEdit = () => {
    setEditingTabId(null);
    setEditingTabName('');
  };

  const openDeleteTabDialog = (tab) => {
    if (tabs.length <= 1) {
      setError('You must keep at least one tab');
      return;
    }

    setTabPendingDeletion(tab);
    setMoveFoldersToTabId('');
    setDeleteTabDialogOpen(true);
    setError('');
  };

  const closeDeleteTabDialog = () => {
    if (deletingTab) {
      return;
    }

    setDeleteTabDialogOpen(false);
    setTabPendingDeletion(null);
    setMoveFoldersToTabId('');
  };

  const handleDeleteTab = async () => {
    if (!tabPendingDeletion) {
      return;
    }

    const foldersInTab = folders.filter(
      (folder) => folder.isDeleted !== true && folder.tabId === tabPendingDeletion._id,
    );
    if (tabs.length <= 1) {
      setError('You must keep at least one tab');
      return;
    }
    if (foldersInTab.length > 0 && !moveFoldersToTabId) {
      setError('Choose a tab to move these folders into first');
      return;
    }

    try {
      setDeletingTab(true);
      await axios.delete(`${API_URL}/api/tabs/${tabPendingDeletion._id}`, {
        data: foldersInTab.length > 0 ? { moveToTabId: moveFoldersToTabId } : {},
      });

      const remainingTabs = tabs.filter((tab) => tab._id !== tabPendingDeletion._id);
      const nextActiveTab =
        activeTab === tabPendingDeletion._id ? remainingTabs[0]?._id || null : activeTab;

      if (nextActiveTab) {
        setActiveTab(nextActiveTab);
        setSearchParams({ tab: nextActiveTab }, { replace: true });
      }

      await Promise.all([fetchTabs(), fetchFolders(false)]);
      setDeleteTabDialogOpen(false);
      setTabPendingDeletion(null);
      setMoveFoldersToTabId('');
      setSuccess('Tab deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting tab:', error);
      setError(error.response?.data?.error || error.message || 'Failed to delete tab');
    } finally {
      setDeletingTab(false);
    }
  };

  const handleEditFolder = async () => {
    if (!folderName.trim()) {
      setError('Folder name is required');
      return;
    }

    try {
      const response = await axios.put(`${API_URL}/api/folders/${editingFolder._id}`, {
        name: folderName.trim(),
        tabId: selectedTabId,
      });

      const updatedFolder = response.data;
      trackEvent('update_folder', {
        scope: 'global',
        context: {},
        payload: {
          folderId: updatedFolder._id,
          name: updatedFolder.name,
          tabId: updatedFolder.tabId,
        },
      });
      setFolders(folders.map((f) => (f._id === editingFolder._id ? updatedFolder : f)));
      setFolderName('');
      setSelectedTabId('');
      setEditingFolder(null);
      setEditDialogOpen(false);
      setError('');
      setSuccess('Folder updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating folder:', error);
      setError(error.message || 'Failed to update folder');
    }
  };

  const openEditDialog = (folder) => {
    setEditingFolder(folder);
    setFolderName(folder.name);
    setSelectedTabId(folder.tabId || (tabs.length > 0 ? tabs[0]._id : ''));
    setEditDialogOpen(true);
    setError('');
  };

  const openDeleteFolderDialog = (folder) => {
    setFolderPendingDeletion(folder);
    setDeleteFolderDialogOpen(true);
    setError('');
  };

  const closeDeleteFolderDialog = () => {
    if (deletingFolder) {
      return;
    }

    setDeleteFolderDialogOpen(false);
    setFolderPendingDeletion(null);
  };

  const handleDeleteFolder = async () => {
    if (!folderPendingDeletion) {
      return;
    }

    try {
      setDeletingFolder(true);
      await axios.delete(`${API_URL}/api/folders/${folderPendingDeletion._id}`);
      setFolders((currentFolders) =>
        currentFolders.map((folder) =>
          folder._id === folderPendingDeletion._id ? { ...folder, isDeleted: true } : folder,
        ),
      );
      setDeleteFolderDialogOpen(false);
      setFolderPendingDeletion(null);
      setSuccess('Folder archived successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting folder:', error);
      setError(error.response?.data?.error || error.message || 'Failed to delete folder');
    } finally {
      setDeletingFolder(false);
    }
  };

  const handleOpenTerminationModal = (folder) => {
    setSelectedFolderForTermination(folder);
    setTerminationModalOpen(true);
  };

  const handleCloseTerminationModal = () => {
    setTerminationModalOpen(false);
    setSelectedFolderForTermination(null);
  };

  const closeDialogs = () => {
    setAddDialogOpen(false);
    setEditDialogOpen(false);
    setAddTabDialogOpen(false);
    setFolderName('');
    setTabName('');
    setEditingFolder(null);
    setSelectedTabId('');
    setError('');
  };

  const handleFetchLatest = async () => {
    try {
      await axios.post(`${API_URL}/api/tracked-channels/refresh`);
      setSuccess('Fetching latest data started. This may take a little while.');
      setToastOpen(true);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.message || 'Failed to trigger fetch');
    }
  };

  const handleDragStart = (event) => {
    setActiveDragId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveDragId(null);

    if (!over) return;

    // Handle Tab Reordering
    if (active.data.current?.type === 'tab') {
      if (active.id !== over.id) {
        setTabs((items) => {
          const oldIndex = items.findIndex((t) => t._id === active.id);
          const newIndex = items.findIndex((t) => t._id === over.id);

          if (oldIndex === -1 || newIndex === -1) return items;

          const newTabs = arrayMove(items, oldIndex, newIndex);

          // Persist order
          const reorderedTabs = newTabs.map((tab, index) => ({
            _id: tab._id,
            order: index,
          }));

          axios.put(`${API_URL}/api/tabs/reorder/all`, { tabs: reorderedTabs }).catch((err) => {
            console.error('Failed to persist tab order', err);
            // Ideally we'd revert here, but simpler to just log
          });

          return newTabs;
        });
      }
      return;
    }

    // Check if we dropped onto a tab
    const isTabDrop = tabs.some((t) => t._id === over.id);

    if (isTabDrop) {
      // Don't process if active item is being dropped on its current tab view
      // But we need to be careful: visual feedback is good.
      // Check if the folder already belongs to this tab to avoid unnecessary API calls
      const folder = folders.find((f) => f._id === active.id);
      const targetTabId = over.id;

      if (folder && folder.tabId !== targetTabId) {
        const targetTabName = tabs.find((t) => t._id === over.id)?.name || 'Tab';

        // Update state optimistically
        const updatedFolder = { ...folder, tabId: targetTabId };
        setFolders(folders.map((f) => (f._id === active.id ? updatedFolder : f)));
        setSuccess(`Moved to ${targetTabName}`);
        setToastOpen(true);
        setTimeout(() => setSuccess(''), 3000);

        try {
          await axios.put(`${API_URL}/api/folders/${active.id}`, {
            name: folder.name,
            tabId: targetTabId,
          });
          trackEvent('update_folder', {
            scope: 'global',
            context: {},
            payload: {
              folderId: active.id,
              name: folder.name,
              tabId: targetTabId,
              movedToTab: true,
            },
          });
        } catch (error) {
          console.error('Failed to move folder to tab:', error);
          fetchFolders(); // Revert on error
        }
      }
      return;
    }

    // Handle normal reordering within the grid
    if (active.id !== over.id) {
      setFolders((items) => {
        const foldersInCurrentTab = items.filter(
          (item) => item.isDeleted !== true && item.tabId === activeTab,
        );
        const oldIndex = foldersInCurrentTab.findIndex((item) => item._id === active.id);
        const newIndex = foldersInCurrentTab.findIndex((item) => item._id === over.id);

        if (oldIndex === -1 || newIndex === -1) {
          return items;
        }

        const reorderedVisibleFolders = arrayMove(foldersInCurrentTab, oldIndex, newIndex);
        const reorderedFolders = reorderedVisibleFolders.map((folder, index) => ({
          _id: folder._id,
          order: index,
          tabId: folder.tabId,
        }));
        const reorderedFolderMap = new Map(
          reorderedVisibleFolders.map((folder, index) => [folder._id, { ...folder, order: index }]),
        );
        const newItems = items.map((item) => reorderedFolderMap.get(item._id) || item);

        // Send to backend
        axios
          .put(`${API_URL}/api/folders/reorder/all`, { folders: reorderedFolders })
          .then(() => {
            trackEvent('reorder_folders', {
              scope: 'global',
              context: {},
              payload: { count: reorderedFolders.length },
            });
          })
          .catch((err) => console.error('Failed to persist order', err));

        return newItems;
      });
    }
  };

  // Custom collision detection strategy
  const customCollisionDetection = useCallback((args) => {
    // 1. Check if pointer is over any tabs (using pointerWithin for better tab detection)
    const pointerIntersections = pointerWithin(args);
    const tabCollision = pointerIntersections.find(
      (collision) => collision.data?.current?.type === 'tab',
    );

    if (tabCollision) {
      return [tabCollision];
    }

    // 2. Fallback to closest center for sorting items
    return closestCenter(args);
  }, []);

  const activeFolders = folders.filter((folder) => folder.isDeleted !== true);
  const archivedFolders = folders.filter((folder) => folder.isDeleted === true);
  const visibleFolders =
    activeTab === ARCHIVED_TAB_ID
      ? archivedFolders
      : activeFolders.filter((folder) => folder.tabId === activeTab);

  const activeFolder = activeDragId ? folders.find((f) => f._id === activeDragId) : null;

  return (
    <Container maxWidth="xl">
      <StyledContainer>
        <DndContext
          sensors={sensors}
          collisionDetection={customCollisionDetection}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          {/* Page Hero */}
          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'stretch', md: 'center' },
                justifyContent: 'space-between',
                gap: 3,
                flexDirection: { xs: 'column', md: 'row' },
              }}
            >
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    mb: 1,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Your Folders
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Group channels into folders to compare performance and track new uploads fast.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {isZKeyHeld && (
                  <Tooltip
                    title="Data is automatically refetched every 12 hours but if you'd like the latest data now, click here"
                    componentsProps={{
                      tooltip: {
                        sx: { padding: '10px 14px' },
                      },
                    }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<Refresh />}
                      onClick={handleFetchLatest}
                      sx={{ borderRadius: 3, padding: '9px 16px' }}
                    >
                      Fetch Latest Data
                    </Button>
                  </Tooltip>
                )}
                <StyledChannelChip label={`${activeFolders.length} Folders`} />
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => {
                    setAddDialogOpen(true);
                    setError('');
                    // Default to active tab
                    if (activeTab) {
                      setSelectedTabId(activeTab);
                    } else if (tabs.length > 0) {
                      setSelectedTabId(tabs[0]._id);
                    } else {
                      setSelectedTabId('');
                    }
                  }}
                  sx={{ borderRadius: 3, padding: '10px 24px' }}
                >
                  New Folder
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Tabs */}
          <Box
            sx={{
              mb: 3,
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <SortableContext
              items={tabs.map((t) => t._id)}
              strategy={horizontalListSortingStrategy}
            >
              <Tabs
                value={activeTab}
                onChange={(e, val) => {
                  // Don't allow switching tabs while editing
                  if (!editingTabId) {
                    setActiveTab(val);
                    setSearchParams({ tab: val }, { replace: true });
                  }
                }}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ flex: 1 }}
              >
                {tabs.map((tab) => (
                  <SortableTab
                    key={tab._id}
                    label={tab.name}
                    value={tab._id}
                    isEditing={editingTabId === tab._id}
                    editingName={editingTabName}
                    canDelete={tabs.length > 1}
                    onDoubleClick={() => handleTabDoubleClick(tab._id)}
                    onEditChange={setEditingTabName}
                    onEditSave={handleUpdateTab}
                    onEditCancel={handleCancelTabEdit}
                    onDelete={() => openDeleteTabDialog(tab)}
                  />
                ))}
                {isZKeyHeld && (
                  <Tab
                    label={`Archived${archivedFolders.length > 0 ? ` (${archivedFolders.length})` : ''}`}
                    value={ARCHIVED_TAB_ID}
                    sx={{
                      opacity: activeTab === ARCHIVED_TAB_ID ? 1 : 0.72,
                      color: activeTab === ARCHIVED_TAB_ID ? 'primary.main' : 'text.secondary',
                    }}
                  />
                )}
              </Tabs>
            </SortableContext>
            <Button
              onClick={() => setAddTabDialogOpen(true)}
              startIcon={<Add />}
              sx={{
                ml: 1,
                minWidth: 'auto',
                px: 1.5,
                textTransform: 'none',
                color: 'text.secondary',
                '&:hover': {
                  color: 'text.primary',
                  backgroundColor: 'rgba(255,255,255,0.04)',
                },
              }}
            >
              Add Tab
            </Button>
          </Box>

          {success && (
            <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }}>
              {success}
            </Alert>
          )}
          <Snackbar
            open={toastOpen}
            autoHideDuration={3000}
            onClose={() => setToastOpen(false)}
            message={success || 'Success'}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            ContentProps={{
              sx: {
                color: 'white',
              },
            }}
          />
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
              {error}
            </Alert>
          )}

          <StyledPaper>
            {loading || loadingTabs ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : visibleFolders.length === 0 ? (
              <Box
                sx={{
                  textAlign: 'center',
                  paddingTop: 8,
                  paddingBottom: 8,
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    margin: '0 auto',
                    marginBottom: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background:
                      'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
                    border: '2px solid rgba(148, 163, 184, 0.2)',
                  }}
                >
                  <FolderIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                </Box>
                <Typography variant="h6" color="text.secondary" mb={1}>
                  {activeTab === ARCHIVED_TAB_ID ? 'No archived folders found' : 'No folders found'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {activeTab === ARCHIVED_TAB_ID
                    ? 'Archived folders will appear here'
                    : 'Create a folder to organize your tracked channels'}
                </Typography>
              </Box>
            ) : (
              <SortableContext
                items={visibleFolders.map((f) => f._id)}
                strategy={rectSortingStrategy}
              >
                <Grid container spacing={4} sx={{ justifyContent: 'flex-start' }}>
                  {visibleFolders.map((folder) => (
                    <SortableFolderCard
                      key={folder._id}
                      folder={folder}
                      openEditDialog={openEditDialog}
                      onDeleteFolder={openDeleteFolderDialog}
                      onOpenTerminationModal={handleOpenTerminationModal}
                      showTerminationSummary={showTerminationSummary}
                    />
                  ))}
                </Grid>
              </SortableContext>
            )}
          </StyledPaper>

          {/* Drag Overlay for smoother visual feedback */}
          <DragOverlay>
            {activeFolder ? (
              <Box sx={{ width: 300, opacity: 0.9 }}>
                <FolderOverlay folder={activeFolder} />
              </Box>
            ) : null}
          </DragOverlay>
        </DndContext>
      </StyledContainer>

      {/* Add Folder Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={closeDialogs}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background:
              'linear-gradient(145deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)',
            border: '1px solid rgba(148, 163, 184, 0.1)',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Add />
            <Typography variant="h6">Add New Folder</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            variant="outlined"
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            sx={{
              mt: 2,
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
          <FormControl fullWidth>
            <InputLabel>Tab</InputLabel>
            <Select
              value={selectedTabId}
              label="Tab"
              onChange={(e) => setSelectedTabId(e.target.value)}
            >
              {tabs.map((tab) => (
                <MenuItem key={tab._id} value={tab._id}>
                  {tab.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={closeDialogs} startIcon={<Close />}>
            Cancel
          </Button>
          <Button
            onClick={handleAddFolder}
            variant="contained"
            startIcon={<Check />}
            sx={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                opacity: 0.9,
              },
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Folder Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={closeDialogs}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background:
              'linear-gradient(145deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)',
            border: '1px solid rgba(148, 163, 184, 0.1)',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Create />
            <Typography variant="h6">Edit Folder</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            variant="outlined"
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            sx={{
              mt: 2,
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
          <FormControl fullWidth>
            <InputLabel>Tab</InputLabel>
            <Select
              value={selectedTabId}
              label="Tab"
              onChange={(e) => setSelectedTabId(e.target.value)}
            >
              {tabs.map((tab) => (
                <MenuItem key={tab._id} value={tab._id}>
                  {tab.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={closeDialogs} startIcon={<Close />}>
            Cancel
          </Button>
          <Button
            onClick={handleEditFolder}
            variant="contained"
            startIcon={<Check />}
            sx={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                opacity: 0.9,
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Tab Dialog */}
      <Dialog
        open={addTabDialogOpen}
        onClose={closeDialogs}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background:
              'linear-gradient(145deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)',
            border: '1px solid rgba(148, 163, 184, 0.1)',
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6">Add New Tab</Typography>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Tab Name"
            fullWidth
            variant="outlined"
            value={tabName}
            onChange={(e) => setTabName(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialogs}>Cancel</Button>
          <Button onClick={handleAddTab} variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Tab Dialog */}
      <Dialog
        open={deleteTabDialogOpen}
        onClose={closeDeleteTabDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background:
              'linear-gradient(145deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)',
            border: '1px solid rgba(148, 163, 184, 0.1)',
          },
        }}
      >
        <DialogTitle>Delete Tab</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to delete <strong>{tabPendingDeletion?.name}</strong>?
          </Typography>
          {tabPendingDeletion &&
            (() => {
              const foldersInTab = folders.filter(
                (folder) => folder.isDeleted !== true && folder.tabId === tabPendingDeletion._id,
              );
              const availableDestinationTabs = tabs.filter((tab) => tab._id !== tabPendingDeletion._id);

              if (foldersInTab.length === 0) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    This tab is empty, so it can be deleted immediately.
                  </Typography>
                );
              }

              return (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    This tab still contains {foldersInTab.length}{' '}
                    {foldersInTab.length === 1 ? 'folder' : 'folders'}. Move them to another tab
                    before deleting it.
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel>Move folders to</InputLabel>
                    <Select
                      value={moveFoldersToTabId}
                      label="Move folders to"
                      onChange={(e) => setMoveFoldersToTabId(e.target.value)}
                    >
                      {availableDestinationTabs.map((tab) => (
                        <MenuItem key={tab._id} value={tab._id}>
                          {tab.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              );
            })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteTabDialog} disabled={deletingTab}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteTab}
            color="error"
            variant="contained"
            disabled={
              deletingTab ||
              (tabPendingDeletion &&
                folders.some(
                  (folder) =>
                    folder.isDeleted !== true && folder.tabId === tabPendingDeletion._id,
                ) &&
                !moveFoldersToTabId)
            }
            startIcon={deletingTab ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            Delete Tab
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Folder Dialog */}
      <Dialog
        open={deleteFolderDialogOpen}
        onClose={closeDeleteFolderDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background:
              'linear-gradient(145deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)',
            border: '1px solid rgba(148, 163, 184, 0.1)',
          },
        }}
      >
        <DialogTitle>Archive Folder</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to archive <strong>{folderPendingDeletion?.name}</strong>? This action cannot be undone!
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteFolderDialog} disabled={deletingFolder}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteFolder}
            color="error"
            variant="contained"
            disabled={deletingFolder}
            startIcon={
              deletingFolder ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />
            }
          >
            Archive Folder
          </Button>
        </DialogActions>
      </Dialog>

      <TerminationModal
        open={terminationModalOpen}
        onClose={handleCloseTerminationModal}
        folderId={selectedFolderForTermination?._id}
        folderName={selectedFolderForTermination?.name}
      />
    </Container>
  );
}

export default ChannelTracker;
